import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSetManufacturerStoreHidden } from "@/api/manufacturer-tracking";
import { queryKeys } from "@/api/queries";
import { ActionPopover } from "@/components/action-popover";
import { LoadingButton } from "@/components/loading-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/api-error";
import { parseUTCDate } from "@/lib/utils";

const POLL_MS = 5_000;

function products(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? "product" : "products"}`;
}

/**
 * Hide a manufacturer we are not ready to sell, such as one whose account is still being set up
 * (USA-1460): its brand page and products come off the store until it is shown again.
 */
export function StoreVisibilityPanel({
  manufacturerUuid,
  manufacturerName,
  hiddenAt,
  pendingProductCount,
}: {
  manufacturerUuid: string;
  manufacturerName: string;
  hiddenAt: string | null;
  pendingProductCount: number;
}) {
  const setHidden = useSetManufacturerStoreHidden(manufacturerUuid);
  const queryClient = useQueryClient();
  const hidden = hiddenAt !== null;

  // The products move in the background; follow them until they are done.
  useEffect(() => {
    if (pendingProductCount === 0) return;
    const timer = window.setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.manufacturers.detail(manufacturerUuid),
      });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [pendingProductCount, manufacturerUuid, queryClient]);

  function apply(nextHidden: boolean, close?: () => void) {
    setHidden.mutate(nextHidden, {
      onSuccess: () => {
        close?.();
        toast.success(
          nextHidden
            ? `${manufacturerName} hidden from the store`
            : `${manufacturerName} back on the store`,
        );
      },
      onError: (error) =>
        toast.error(
          getApiErrorMessage(
            error,
            nextHidden
              ? `Could not hide ${manufacturerName}`
              : `Could not show ${manufacturerName}`,
          ),
        ),
    });
  }

  return (
    <section
      aria-labelledby="mfr-store-visibility"
      className="overflow-hidden rounded-xl border bg-card"
    >
      <div className="border-b px-4 py-2">
        <h2 id="mfr-store-visibility" className="text-sm font-semibold">
          Store
        </h2>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div
          aria-live="polite"
          className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm"
        >
          {hidden ? (
            <>
              <Badge variant="secondary">Hidden</Badge>
              <span className="text-muted-foreground">
                Since {parseUTCDate(hiddenAt).toLocaleDateString()}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">On the store</span>
          )}
          {pendingProductCount > 0 && (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
              {hidden ? "Hiding" : "Showing"} {products(pendingProductCount)}
            </span>
          )}
        </div>
        {hidden ? (
          <LoadingButton
            size="sm"
            loading={setHidden.isPending}
            loadingText="Showing..."
            onClick={() => apply(false)}
          >
            Show on store
          </LoadingButton>
        ) : (
          <ActionPopover
            align="end"
            trigger={
              <Button variant="outline" size="sm">
                Hide from store
              </Button>
            }
            title={`Hide ${manufacturerName} from the store?`}
            confirmLabel="Hide"
            pending={setHidden.isPending}
            pendingLabel="Hiding..."
            onConfirm={(_note, close) => apply(true, close)}
          >
            <p className="text-xs text-muted-foreground">
              Its brand page and products come off the store until you show them
              again.
            </p>
          </ActionPopover>
        )}
      </div>
    </section>
  );
}
